import {
    insights_dialogTitle,
    insights_feedbackLauncherLink,
    insights_moreFeedback,
    insights_unrelatedOption,
    insights_relatedNotUsefulOption,
    insights_notRelatedOption,
    insights_missingSuggestionOption,
    insights_hardToUseOptionOption,
    insights_donationTextTooltip,
    insights_thankyouText,
    insights_subTitleText,
} from './FeedbackContainer.locstring.json';
import { observer } from 'mobx-react-lite';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import loc from 'owa-localize';
import * as React from 'react';
import { FeedbackRibbon, FeedbackDialogParams, FeedbackCategory } from 'owa-feedback';
import type {
    InsightsDataFetchSource,
    RelevanceRelationship,
} from 'owa-meeting-insights-types-and-flights';

import { donationDescription } from 'owa-locstrings/lib/strings/donationdescription.locstring.json';
import { donationText } from 'owa-locstrings/lib/strings/donationtext.locstring.json';
import { feedbackCommentsPlaceholder } from 'owa-locstrings/lib/strings/feedbackcommentsplaceholder.locstring.json';
import { feedbackThankYouMessage } from 'owa-locstrings/lib/strings/feedbackthankyoumessage.locstring.json';
import { privacyPolicyLinkText } from 'owa-locstrings/lib/strings/privacypolicylinktext.locstring.json';
import { default as sendFeeback, getDonatedJson } from '../helpers/sendFeedbackHelper';
import { logFeedbackButtonClicked } from '../helpers/instrumentationHelpers';
import { NATIVE, getHostValue } from 'owa-config';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';

import styles from './RelatedContentCommonStyles.scss';

export interface FeedbackContainerProps {
    colorScheme: ColorScheme;
    traceId: string;
    feedbackType: FeedbackType;
    isOrganizer: boolean;
    relevanceRelationship: RelevanceRelationship;
    referenceId: string;
    entrySource: InsightsDataFetchSource;
    meetingId: string;
    itemId: string;
}

export enum FeedbackType {
    Unknown = 0,
    Email = 1,
    File = 2,
    Event = 3,
}

function getFeedbackStrings() {
    return {
        dialogTitle: loc(insights_dialogTitle),
        feedbackCommentsPlaceholder: loc(feedbackCommentsPlaceholder),
        feedbackLauncherLink: loc(insights_feedbackLauncherLink),
        searchFeedbackThankYouMessage: loc(feedbackThankYouMessage),
        moreFeedback: loc(insights_moreFeedback),
        unrelatedOption: loc(insights_unrelatedOption),
        relatedNotUsefulOption: loc(insights_relatedNotUsefulOption),
        notRelatedOption: loc(insights_notRelatedOption),
        missingSuggestionOption: loc(insights_missingSuggestionOption),
        hardToUseOption: loc(insights_hardToUseOptionOption),
        donationText: loc(donationText),
        donationTextTooltip: loc(insights_donationTextTooltip),
        secondaryDonationText: loc(donationDescription),
        privacyPolicyLinkText: loc(privacyPolicyLinkText),
        thankyouText: loc(insights_thankyouText),
        subtitleText: loc(insights_subTitleText),
    };
}

export default observer(function FeedbackContainer(props: FeedbackContainerProps) {
    const feedbackStrings = getFeedbackStrings();

    const unrelatedOption = 'The suggested item is unrelated to the meeting';
    const relatedNotUsefulOption = 'The suggested item is related but not useful';
    const notRelatedOption = 'The suggested item is inappropriate';
    const missingSuggestionOption = 'An expected item is not displayed';
    const hardToUseOption = 'The feature is hard to use';

    const feedbackCategories: FeedbackCategory[] = [
        {
            logString: unrelatedOption,
            uiString: feedbackStrings.unrelatedOption,
        },
        {
            logString: relatedNotUsefulOption,
            uiString: feedbackStrings.relatedNotUsefulOption,
        },
        {
            logString: notRelatedOption,
            uiString: feedbackStrings.notRelatedOption,
        },
        {
            logString: missingSuggestionOption,
            uiString: feedbackStrings.missingSuggestionOption,
        },
        { logString: hardToUseOption, uiString: feedbackStrings.hardToUseOption },
    ];

    const onDonationCallback = (isContentDonated: boolean) => {
        // This callback gets executed ONLY when the user consents to donate extra information
        sendFeeback(props.traceId, isContentDonated);
    };

    const shouldShowCustomThankYouMessage = (
        inlineFeedbackResponseSelectedCategories: string[]
    ): boolean => {
        return true;
    };

    let hostString: string;
    const hostValue = getHostValue();

    // getHostValue will call getOpxHostApp which returns a default value of '(none)' when the app is not
    // hosted by an Opx Host
    if (hostValue == '(none)') {
        hostString = 'OWA';
    } else if (hostValue == NATIVE) {
        hostString = isHostAppFeatureEnabled('monarchFeedback') ? 'Monarch' : 'OWA';
    } else {
        hostString = hostValue;
    }

    const feedbackDialogParams: FeedbackDialogParams = {
        title: feedbackStrings.dialogTitle,
        subTitle: feedbackStrings.subtitleText,
        categories: feedbackCategories,
        ghostText: feedbackStrings.searchFeedbackThankYouMessage,
        donationText: feedbackStrings.donationText,
        donationToolTip: feedbackStrings.donationTextTooltip,
        donationJson: getDonatedJson(props.traceId),
        onDonationCallback: onDonationCallback,
        secondaryDonationText: feedbackStrings.secondaryDonationText,
        privacyPolicyLinkText: feedbackStrings.privacyPolicyLinkText,
        privacyPolicyLink: 'https://go.microsoft.com/fwlink/?LinkID=746667',
        thankYouDialogParams: {
            thankYouMessage: feedbackStrings.thankyouText,
            shouldShowCustomThankYouMessage: shouldShowCustomThankYouMessage,
        },
        odsFeedbackOverrides: {
            symptomName: 'Recommendations',
            payloadName: 'ServiceCalls',
            clientName: hostString,
            complianceClassification: 'Metadata',
            extraParameters: [
                {
                    Name: 'ItemDetails',
                    Value: {
                        itemId: props.itemId,
                    },
                    ComplianceClassification: 'Metadata',
                },
            ],
        },
    };

    const onYesSelected = () => {
        onFeedbackButtonClicked(true /*isUseful*/);
    };

    const onNoSelected = () => {
        onFeedbackButtonClicked(false /*isUseful*/);
    };

    const onFeedbackButtonClicked = (isUseful: boolean) => {
        logFeedbackButtonClicked(
            props.meetingId,
            isUseful,
            props.traceId,
            props.referenceId,
            props.isOrganizer,
            props.relevanceRelationship,
            props.entrySource,
            props.feedbackType
        );
    };

    return (
        <div className={styles.feedbackContainer}>
            {
                <FeedbackRibbon
                    feature="MeetingInsights"
                    subFeature="Recommendations"
                    ribbonMessage={feedbackStrings.feedbackLauncherLink}
                    moreFeedbackRibbonMessage={feedbackStrings.moreFeedback}
                    thankYouMessage={feedbackStrings.searchFeedbackThankYouMessage}
                    noFeedbackDialogParams={feedbackDialogParams}
                    ribbonMessageClassName={styles.feedbackContainer}
                    yesCallBack={onYesSelected}
                    noCallBack={onNoSelected}
                />
            }
        </div>
    );
});
